import React, { useState, useEffect } from "react";
import { getTimezoneAbbr, TIMEZONE_OPTIONS, APP_DATA_TIMEZONE } from "utils/datetime";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setMode, logoutUser, setViewAsRole, setTimezone } from "state";
import {
  AppBar,
  useTheme,
  Toolbar,
  Menu,
  MenuItem,
  Divider,
  Button,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  LightModeOutlined,
  DarkModeOutlined,
  SettingsOutlined,
  ArrowDropDownOutlined,
  PhoneOutlined,
  VisibilityOffOutlined,
  PublicOutlined,
  CheckOutlined,
} from "@mui/icons-material";

import FlexBetween from "./FlexBetween";
import NotificationMenu from "./NotificationMenu";
import NavbarInterviewClock from "./NavbarInterviewClock";
import SettingsDialog from "./SettingsDialog";
import { VIEW_AS_HOME } from "./ViewAsBanner";
import { getRoleDisplayName } from "utils/roleDisplay";
import { BIDDER_ROLE, isRoleDisabled } from "constants/authRoles";
import profileImage from "assets/profile.jpeg";
import axios from "api/axios";

const VIEW_AS_OPTIONS = [
  { role: "caller", label: "View as Caller", icon: <PhoneOutlined sx={{ fontSize: 18 }} /> },
].filter((opt) => !isRoleDisabled(opt.role));

const Navbar = ({ user, isSidebarOpen, setIsSidebarOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isNonMobile = useMediaQuery("(min-width: 600px)");
  const realUser = useSelector((s) => s.global.realUser || s.global.user);
  const viewAsRole = useSelector((s) => s.global.viewAsRole);
  const timezone = useSelector((s) => s.global.timezone);
  const [anchorEl, setAnchorEl] = useState(null);
  const [tzAnchor, setTzAnchor] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState(user?.avatar || profileImage);

  useEffect(() => {
    if (user?.avatar) {
      setAvatarSrc(user.avatar);
      return;
    }
    let cancelled = false;
    const loadAvatar = () => {
      axios
        .get("/auth/avatar")
        .then((res) => {
          if (!cancelled && res.data?.avatar) setAvatarSrc(res.data.avatar);
        })
        .catch(() => {
          if (!cancelled) setAvatarSrc(profileImage);
        });
    };
    const idleId = window.requestIdleCallback
      ? window.requestIdleCallback(loadAvatar, { timeout: 2500 })
      : window.setTimeout(loadAvatar, 400);
    return () => {
      cancelled = true;
      if (window.requestIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, [user?._id, user?.avatar]);

  const isAdmin = ["admin", "superadmin"].includes(realUser?.role);

  // Add scroll listener for shadow effect
  useEffect(() => {
    const handleScroll = (e) => {
      // Check the scroll position of the main content container
      const target = e?.target;
      const scrollTop = target?.scrollTop || window.scrollY || 0;
      setScrolled(scrollTop > 10);
    };

    // Get the main content container by ID
    const mainContent = document.getElementById("main-content-scroll");
    
    if (mainContent) {
      mainContent.addEventListener("scroll", handleScroll, { passive: true });
    }
    
    // Also listen to window scroll as fallback
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (mainContent) {
        mainContent.removeEventListener("scroll", handleScroll);
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // ⛔ DO NOT put guard above hooks — this is the correct location
  if (!user) {
    return null;
  }

  const isOpen = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleViewAs = (role) => {
    handleClose();
    if (viewAsRole === role) return;
    dispatch(setViewAsRole(role));
    const target = VIEW_AS_HOME[role] || "/dashboard";
    navigate(target);
    window.location.assign(target);
  };

  const handleExitViewAs = () => {
    handleClose();
    dispatch(setViewAsRole(null));
    navigate("/dashboard");
    window.location.assign("/dashboard");
  };

  const handleLogout = async () => {
    handleClose(); // Close menu first
    
    // Clear Redux state immediately
    dispatch(logoutUser());
    
    // Set flag to prevent auth checks
    sessionStorage.setItem("logout", "true");
    localStorage.removeItem("persist:root"); // Clear any persisted state
    
    try {
      // Call logout API to clear server-side cookie
      await axios.post("/auth/logout");
    } catch (err) {
      console.error("Logout API call failed:", err);
      // Continue anyway - we'll clear client-side
    }
    
    // Clear any cookies manually (client-side)
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.endurovateam.com;";
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=endurovateam.com;";
    document.cookie = "token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    
    // Force full page reload to clear all state
    window.location.replace("/login");
  };

  return (
    <AppBar 
      sx={{ 
        position: "static", 
        background: scrolled 
          ? theme.palette.background.alt 
          : "none", 
        boxShadow: scrolled 
          ? "0 2px 10px rgba(0, 0, 0, 0.1)" 
          : "none",
        transition: "all 0.3s ease",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between" }}>
        {/* LEFT SIDE - Empty placeholder for alignment */}
        <FlexBetween>
          {/* Hamburger menu moved to floating Fab in Layout */}
        </FlexBetween>

        {/* RIGHT SIDE */}
        <FlexBetween gap={{ xs: "0.5rem", sm: "1rem", md: "1.5rem" }}>
          <NavbarInterviewClock />

          <IconButton onClick={() => dispatch(setMode())}>
            {theme.palette.mode === "dark" ? (
              <DarkModeOutlined sx={{ fontSize: { xs: 20, sm: 25 } }} />
            ) : (
              <LightModeOutlined sx={{ fontSize: { xs: 20, sm: 25 } }} />
            )}
          </IconButton>

          <NotificationMenu />

          <Button
            onClick={(e) => setTzAnchor(e.currentTarget)}
            startIcon={<PublicOutlined sx={{ fontSize: 16 }} />}
            endIcon={<ArrowDropDownOutlined sx={{ fontSize: 18 }} />}
            title="Display timezone — data is stored in Central Time"
            sx={{
              textTransform: "none",
              color: theme.palette.text.secondary,
              fontWeight: 700,
              fontSize: "0.7rem",
              minWidth: "auto",
              px: { xs: 0.5, sm: 1 },
              "& .MuiButton-startIcon": { mr: 0.5 },
              "& .MuiButton-endIcon": { ml: 0 },
            }}
          >
            {getTimezoneAbbr(timezone)}
          </Button>
          <Menu
            anchorEl={tzAnchor}
            open={Boolean(tzAnchor)}
            onClose={() => setTzAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { maxHeight: 380, width: 260 } }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Display timezone
              </Typography>
              <Typography sx={{ fontSize: "0.68rem", color: theme.palette.text.secondary, mt: 0.25 }}>
                Data is entered & stored in Central Time.
              </Typography>
            </Box>
            <Divider />
            {TIMEZONE_OPTIONS.map((opt) => {
              const selected = opt.value === timezone;
              return (
                <MenuItem
                  key={opt.value}
                  selected={selected}
                  onClick={() => {
                    dispatch(setTimezone(opt.value));
                    setTzAnchor(null);
                  }}
                  sx={{ fontSize: "0.8rem", gap: 1 }}
                >
                  <Box sx={{ width: 18, display: "flex", alignItems: "center" }}>
                    {selected && <CheckOutlined sx={{ fontSize: 16, color: theme.palette.primary.main }} />}
                  </Box>
                  <Box sx={{ flex: 1 }}>{opt.label}</Box>
                  {opt.value === APP_DATA_TIMEZONE && (
                    <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: theme.palette.primary.main }}>
                      DEFAULT
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </Menu>

          <IconButton onClick={() => setSettingsOpen(true)}>
            <SettingsOutlined sx={{ fontSize: { xs: 20, sm: 25 } }} />
          </IconButton>

          <FlexBetween>
            <Button
              onClick={handleClick}
              sx={{
                display: "flex",
                alignItems: "center",
                textTransform: "none",
                gap: { xs: "0.25rem", sm: "0.5rem", md: "1rem" },
                minWidth: "auto",
                px: { xs: 0.5, sm: 1 },
              }}
            >
              <Box
                component="img"
                alt="profile"
                src={avatarSrc}
                height={{ xs: "28px", sm: "32px" }}
                width={{ xs: "28px", sm: "32px" }}
                borderRadius="50%"
                sx={{ objectFit: "cover" }}
              />

              {/* Hide name/role on mobile */}
              {isNonMobile && (
                <Box textAlign="left">
                  <Typography
                    fontWeight="bold"
                    fontSize="0.85rem"
                    sx={{ color: theme.palette.text.primary }}
                  >
                    {user.name}
                  </Typography>
                  <Typography
                    fontSize="0.75rem"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {viewAsRole ? `Preview · ${getRoleDisplayName(user.role)}` : getRoleDisplayName(user.role, { callerTeamLead: user.callerTeamLead })}
                  </Typography>
                </Box>
              )}

              <ArrowDropDownOutlined
                sx={{ color: theme.palette.text.secondary, fontSize: { xs: 20, sm: 25 } }}
              />
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  minWidth: 220,
                  maxWidth: 280,
                  borderRadius: "8px",
                  mt: 0.5,
                },
              }}
            >
              {isAdmin && (
                <>
                  {VIEW_AS_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt.role}
                      onClick={() => handleViewAs(opt.role)}
                      selected={viewAsRole === opt.role}
                      sx={{ gap: 1, fontSize: "0.85rem" }}
                    >
                      {opt.icon}
                      {opt.label}
                    </MenuItem>
                  ))}
                  {viewAsRole && (
                    <MenuItem onClick={handleExitViewAs} sx={{ gap: 1, fontSize: "0.85rem" }}>
                      <VisibilityOffOutlined sx={{ fontSize: 18 }} />
                      Exit view as
                    </MenuItem>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                </>
              )}
              <MenuItem
                onClick={() => {
                  handleClose();
                  handleLogout();
                }}
              >
                Log Out
              </MenuItem>
            </Menu>
          </FlexBetween>
        </FlexBetween>
      </Toolbar>

      <SettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} user={user} />
    </AppBar>
  );
};

export default Navbar;
